class LmdlShippingEstimator extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-subtotal-cents',
      'data-mondial-france-price-cents',
      'data-mondial-belgium-price-cents',
      'data-laposte-france-price-cents',
      'data-laposte-belgium-price-cents',
      'data-mondial-free-shipping',
      'data-selected-carrier',
      'data-selected-country'
    ];
  }

  connectedCallback() {
    if (!this._bound) {
      this._bound = true;
      this.addEventListener('click', (event) => {
        const countryButton = event.target.closest('[data-lmdl-country]');
        if (countryButton && this.contains(countryButton)) {
          this.setCountry(countryButton.dataset.lmdlCountry);
          return;
        }

        const carrierButton = event.target.closest('[data-lmdl-carrier]');
        if (carrierButton && this.contains(carrierButton)) {
          this.setCarrier(carrierButton.dataset.lmdlCarrier);
        }
      });
    }

    let country = this.dataset.selectedCountry === 'BE' ? 'BE' : 'FR';
    let carrier = this.dataset.selectedCarrier === 'laposte' ? 'laposte' : 'mondial';

    try {
      const storedCountry = window.localStorage.getItem('lmdl-shipping-country');
      const storedCarrier = window.localStorage.getItem('lmdl-shipping-carrier');
      if (storedCountry === 'FR' || storedCountry === 'BE') country = storedCountry;
      if (storedCarrier === 'mondial' || storedCarrier === 'laposte') carrier = storedCarrier;
    } catch (_) {}

    this._country = country;
    this._carrier = carrier;
    this.renderValues();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    window.requestAnimationFrame(() => this.renderValues());
  }

  formatMoney(cents) {
    const locale = this.dataset.locale || 'fr-FR';
    const currency = this.dataset.currency || 'EUR';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  getPrice(carrier = this._carrier, country = this._country) {
    const countryKey = country === 'BE' ? 'Belgium' : 'France';
    const carrierKey = carrier === 'laposte' ? 'laposte' : 'mondial';
    const datasetKey = `${carrierKey}${countryKey}PriceCents`;
    return Number(this.dataset[datasetKey] || 0);
  }

  getCarrierButton(carrier = this._carrier) {
    return this.querySelector(`[data-lmdl-carrier="${carrier}"]`);
  }

  getCarrierLabel(carrier = this._carrier) {
    return this.getCarrierButton(carrier)?.dataset.carrierLabel || (carrier === 'laposte' ? 'La Poste – Colissimo' : 'Mondial Relay – Point Relais');
  }

  setCountry(country) {
    this._country = country === 'BE' ? 'BE' : 'FR';
    try { window.localStorage.setItem('lmdl-shipping-country', this._country); } catch (_) {}
    this.renderValues();
  }

  setCarrier(carrier) {
    this._carrier = carrier === 'laposte' ? 'laposte' : 'mondial';
    try { window.localStorage.setItem('lmdl-shipping-carrier', this._carrier); } catch (_) {}
    this.renderValues();
  }

  renderValues() {
    const country = this._country === 'BE' ? 'BE' : 'FR';
    const carrier = this._carrier === 'laposte' ? 'laposte' : 'mondial';
    const countryButtons = [...this.querySelectorAll('[data-lmdl-country]')];
    const carrierButtons = [...this.querySelectorAll('[data-lmdl-carrier]')];
    const shippingValue = this.querySelector('[data-lmdl-shipping-value]');
    const totalValue = this.querySelector('[data-lmdl-total-value]');
    const selectedLabel = this.querySelector('[data-lmdl-selected-carrier-label]');
    if (!countryButtons.length || !carrierButtons.length || !shippingValue || !totalValue) return;

    countryButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.lmdlCountry === country));
    });

    carrierButtons.forEach((button) => {
      const isSelected = button.dataset.lmdlCarrier === carrier;
      button.setAttribute('aria-checked', String(isSelected));
      button.classList.toggle('is-selected', isSelected);
    });

    const freeLabel = this.dataset.freeLabel || 'Offerte';
    const mondialIsFree = this.dataset.mondialFreeShipping === 'true';

    this.querySelectorAll('[data-lmdl-option-price]').forEach((priceNode) => {
      const optionCarrier = priceNode.dataset.lmdlOptionPrice;
      const isFree = optionCarrier === 'mondial' && mondialIsFree;
      const price = this.getPrice(optionCarrier, country);
      priceNode.textContent = isFree ? freeLabel : this.formatMoney(price);
      priceNode.classList.toggle('lmdl-ship-estimator__free', isFree);
    });

    const subtotal = Number(this.dataset.subtotalCents || 0);
    const selectedIsFree = carrier === 'mondial' && mondialIsFree;
    const shipping = selectedIsFree ? 0 : this.getPrice(carrier, country);

    if (selectedLabel) selectedLabel.textContent = this.getCarrierLabel(carrier);
    shippingValue.textContent = selectedIsFree ? freeLabel : this.formatMoney(shipping);
    shippingValue.classList.toggle('lmdl-ship-estimator__free', selectedIsFree);
    totalValue.textContent = this.formatMoney(subtotal + shipping);
  }
}

if (!customElements.get('lmdl-shipping-estimator')) {
  customElements.define('lmdl-shipping-estimator', LmdlShippingEstimator);
}
